import { useCreateOrganizationUserMutation } from '@codezo/graphql';
import { useState } from 'react';
import { toast } from 'sonner';
import { OrganizationSelector } from '../../components/selectors/OrganizationSelector';
import { BirthdayPicker } from '../../components/ui/birthday-picker';
import { Checkbox } from '../../components/ui/checkbox';
import { Button } from '../../components/ui/button';
import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogForm,
	DialogHeader,
	DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';

export function AddContactModal({
	open,
	onOpenChange,
	organizationId,
	onSuccess,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/**
	 * The client to add the contact to. Omit on the global Contacts list — the
	 * modal then asks which client the contact belongs to.
	 */
	organizationId?: string;
	onSuccess?: () => void;
}) {
	const [pickedOrgId, setPickedOrgId] = useState('');
	const [email, setEmail] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [jobPosition, setJobPosition] = useState('');
	const [phone, setPhone] = useState('');
	const [birthday, setBirthday] = useState<string | null>(null);
	const [sendInvitation, setSendInvitation] = useState(true);
	const [notes, setNotes] = useState('');
	const [emailError, setEmailError] = useState<string | null>(null);

	const [createUser, { loading }] = useCreateOrganizationUserMutation();

	const targetOrgId = organizationId ?? pickedOrgId;

	const resetForm = () => {
		setPickedOrgId('');
		setEmail('');
		setFirstName('');
		setLastName('');
		setJobPosition('');
		setPhone('');
		setBirthday(null);
		setSendInvitation(true);
		setNotes('');
		setEmailError(null);
	};

	const handleSubmit = async () => {
		if (!targetOrgId || !email.trim() || !firstName.trim() || !lastName.trim()) return;

		if (!/^\S+@\S+$/.test(email.trim())) {
			setEmailError('Invalid email');
			return;
		}

		try {
			const { data } = await createUser({
				variables: {
					input: {
						organizationId: targetOrgId,
						email: email.trim(),
						firstName: firstName.trim(),
						lastName: lastName.trim(),
						jobPosition: jobPosition.trim() || undefined,
						phone: phone.trim() || undefined,
						birthday: birthday ?? undefined,
						sendInvitation,
						notes: notes.trim() || undefined,
					},
				},
			});

			if (data?.createOrganizationUser?.success) {
				toast.success(
					sendInvitation ? 'Contact invited' : 'Contact added — no invitation sent'
				);
				resetForm();
				onOpenChange(false);
				onSuccess?.();
			}
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to add contact');
		}
	};

	const canSubmit = targetOrgId && email.trim() && firstName.trim() && lastName.trim();

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				if (!next) resetForm();
				onOpenChange(next);
			}}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Add contact</DialogTitle>
					<DialogDescription>
						{organizationId
							? 'Add a new contact to this organization.'
							: 'Add a new contact to one of your clients.'}
					</DialogDescription>
				</DialogHeader>

				<DialogForm onSubmit={handleSubmit}>
					<DialogBody>
						<div className="flex flex-col gap-4">
							{!organizationId && (
								<div className="flex flex-col gap-1.5">
									<Label>
										Client <span className="text-destructive">*</span>
									</Label>
									<OrganizationSelector
										value={pickedOrgId}
										onChange={(v) => setPickedOrgId(v)}
										placeholder="Select client"
										clearable
									/>
								</div>
							)}

							<div className="grid grid-cols-2 gap-3">
								<div className="flex flex-col gap-1.5">
									<Label>
										First name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										placeholder="John"
									/>
								</div>
								<div className="flex flex-col gap-1.5">
									<Label>
										Last name <span className="text-destructive">*</span>
									</Label>
									<Input
										value={lastName}
										onChange={(e) => setLastName(e.target.value)}
										placeholder="Doe"
									/>
								</div>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>
									Email <span className="text-destructive">*</span>
								</Label>
								<Input
									type="email"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										if (emailError) setEmailError(null);
									}}
									placeholder="user@example.com"
								/>
								{emailError && (
									<p className="text-xs text-destructive">{emailError}</p>
								)}
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Job position</Label>
								<Input
									value={jobPosition}
									onChange={(e) => setJobPosition(e.target.value)}
									placeholder="e.g. Head of Procurement"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Phone</Label>
								<Input
									type="tel"
									value={phone}
									onChange={(e) => setPhone(e.target.value)}
									placeholder="+45 12 34 56 78"
								/>
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Birthday</Label>
								<BirthdayPicker value={birthday} onChange={setBirthday} />
							</div>

							<div className="flex flex-col gap-1.5">
								<Label>Internal notes</Label>
								<Textarea
									value={notes}
									onChange={(e) => setNotes(e.target.value)}
									placeholder="Only visible to admins"
									rows={3}
								/>
							</div>

							<label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-line-1 bg-surface p-2.5">
								<Checkbox
									checked={sendInvitation}
									onCheckedChange={(next) => setSendInvitation(next === true)}
									className="mt-0.5"
								/>
								<span className="flex flex-col gap-0.5">
									<span className="text-sm text-text-1">
										Send invitation email
									</span>
									<span className="text-xs text-text-3">
										Off records the contact without emailing them. You can
										invite them later from the contact’s row.
									</span>
								</span>
							</label>
						</div>
					</DialogBody>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								resetForm();
								onOpenChange(false);
							}}
							disabled={loading}
						>
							Cancel
						</Button>
						<Button type="submit" disabled={!canSubmit || loading}>
							{loading
								? 'Adding…'
								: sendInvitation
									? 'Send invitation'
									: 'Add contact'}
						</Button>
					</DialogFooter>
				</DialogForm>
			</DialogContent>
		</Dialog>
	);
}
